<template>
  <main id="main-content">
    <ApiLoading v-if="apiStatus.refData.loading || apiStatus.refData.fail" :apiError="apiStatus.refData.fail" />
    <div class="feature-landing-page" v-else>
      <div class="content-page-banner">
        <div class="banner-content">
          <h1>{{ $route.meta.title }}</h1>
          <div class="underline"></div>
          <div v-html="refData.rebateTierHeadingSubtitleText.informationText" />
        </div>
      </div>
      <Alert v-if="submitPolicyError" :message="ErrorStrings.SUBMIT_POLICY_API_ERROR" alertType="error"/> 
      <section class="multi-step-form">
        <MultiStepFormNavigation
          :currentPage="currentPage"
          :links="navLinks"
          @linkClicked="updateStep"
          :disabled="apiStatus.submit.loading || apiStatus.submit.fail || apiStatus.premium.loading"
        />
        <ApiLoading v-if="apiStatus.submit.loading || apiStatus.submit.fail || apiStatus.premium.loading" :apiError="apiStatus.submit.fail" />
        <div class="form-contents" v-else>
          <!-- Step 1: Rebate tier -->
          <template v-if="isFirstPage">
            <SectionSeparatorWrapper headerText="Rebate tier" headerRequiredFieldsText="Indicates required field">
              <section>
                <FormField labelText="Rebate applicant">
                  <p>{{ rebateApplicantName }}</p>
                </FormField>
                <FormField labelText="Current rebate registration date" :required="false" :helperText="refData.rebateTierCurrentRebateRegistrationDateCalloutText.informationText">
                  <p>{{ reverseFormatFormDate(membershipDetails.rebate.rebateCommencementDate) }}</p>
                </FormField>
                <FormField labelText="New rebate tier effective date" :required="false" :helperText="refData.rebateTierNewRebateTierEffectiveDateCalloutText.informationText">
                  <p>{{ currentDate }}</p>
                </FormField>
              </section>
            </SectionSeparatorWrapper>
            <SectionSeparatorWrapper>
              <h3 class="h6 sub-heading-underlined">Rebate information</h3>
              <!-- Circumstances -->
              <FormField labelText="My circumstances" forId="myCircumstances" :required="true">
                <div class="input-grid">
                  <ul class="radio-group large">
                    <li v-for="item in myCircumstancesOptions" :key="item.value">
                      <input
                        type="radio"
                        v-model="v$.newMyCircumstanceSelected.$model"
                        :id="item.value"
                        :value="item.value"
                        name="newMyCircumstanceSelected"
                        :error="v$.newMyCircumstanceSelected.$error"
                        errorMessage="Please include a response for your circumstances."
                      />
                      <label :for="item.value">{{ item.displayValue }}</label>
                    </li>
                  </ul>
                </div>
              </FormField>
              <!-- Household Income -->
              <FormField
                labelText="My household income for the purposes of determining rebate is"
                forId="rebateTier"
                :required="true"
                v-if="newMyCircumstanceSelected"
                :helperText="refData.rebateTierMyHouseholdIncomeCalloutText.informationText"
              >
                <DropdownSelect
                  :items="getRebateTierList(newMyCircumstanceSelected)"
                  v-model="v$.newRebateTierSelected.$model"
                  :initialSelect="getRebateTierInitialSelect(newRebateTierSelected, newMyCircumstanceSelected)"
                  name="rebate-tier"
                />
                <label v-if="v$.newRebateTierSelected.$error" class="error" for="rebate-tier">
                  <svg viewBox="0 0 28 28">
                    <use xlink:href="/assets/public/images/icons.svg#error" />
                  </svg>
                  {{ `Based on your household income, your rebate tier has not changed.` }}
                </label>
              </FormField>
            </SectionSeparatorWrapper>
          </template>
          <!-- Step 2: Review n submit -->
          <template v-if="isSecondPage">
            <ReviewAndSubmitComponent :updateKey="false">
              <h3 class="review-subheading">Current Rebate details</h3>
              <table class="review-and-submit-table current-rebate-details-table">
                <tbody>
                  <tr>
                    <th>Rebate applicant</th>
                    <td>{{ rebateApplicantName }}</td>
                  </tr>
                  <tr>
                    <th>Current rebate registration date</th>
                    <td>{{ reverseFormatFormDate(membershipDetails.rebate.rebateCommencementDate) }}</td>
                  </tr>
                  <tr>
                    <th>My current rebate tier</th>
                    <td>{{ RebateTierDisplayWithoutIncomeBrackets[currentRebateTier] }}</td>
                  </tr>
                </tbody>
              </table>

              <h3 class="review-subheading">Updated Rebate tier</h3>
              <table class="review-and-submit-table updated-rebate-details-table">
                <tbody>
                  <tr>
                    <th>Effective date</th>
                    <td>{{ currentDate }}</td>
                  </tr>
                  <tr>
                    <th>My circumstances</th>
                    <td>{{ myCircumstancesOptions.find((c) => c.value === newMyCircumstanceSelected).displayValue }}</td>
                  </tr>
                  <tr>
                    <th>My household income for determining rebate is</th>
                    <td>{{ getRebateTierInitialSelect(newRebateTierSelected, newMyCircumstanceSelected) }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="currentPremium && newEstimatedPremium && apiStatus.premium.fail === false">
                <FormField noLabelOverride :helperText="refData.rebateReviewAndSubmitMyPremiumCalloutText.informationText">
                  <h3 class="review-subheading">My Premium</h3>
                  <table class="review-and-submit-table">
                    <tbody>
                      <tr>
                        <th>Current premium</th>
                        <td>{{ `${formatAsCurrency(currentPremium)} / ${mapPaymentFrequencyFromRecurringToSingleton(membershipInfo.frequency)}` }}</td>
                      </tr>
                      <tr class="info">
                        <th>New premium</th>
                        <td>{{ `${formatAsCurrency(newEstimatedPremium)} / ${mapPaymentFrequencyFromRecurringToSingleton(membershipInfo.frequency)}` }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-html="refData.rebateReviewAndSubmitNoteText.informationText" />
                </FormField>
              </div>
              <div v-else>
                <Alert
                  alertType="info"
                  message="We are unable to advise you of your estimated new premium. If you would still like to proceed with the change, your new premium will be sent to you in your certificate of cover."
                />
              </div>
            </ReviewAndSubmitComponent>
          </template>
          <!-- Form buttons -->
          <div class="form-page-nav">
            <section class="form-buttons">
              <button id="back-button" v-if="isSecondPage" class="secondary" @click="handleBackButton">Back</button>
              <button id="next-button" @click.prevent="handleNextButton" :disabled="v$.$invalid">
                {{ isFirstPage ? 'Next' : 'Submit' }}
              </button>
            </section>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { MultiStepFormNavigation, ApiLoading, FormField, SectionSeparatorWrapper, ReviewAndSubmitComponent, Alert } from '@/components';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { getCurrentDate, reverseFormatFormDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { ErrorStrings } from '@general/js/constants';
import { DropdownSelect } from '@general/js/components';
import { RebateTierDisplayWithoutIncomeBrackets } from '@/enums';
import RebateServices from '@/services/MyCover/Rebates';
import { REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import CommonServices from '@/services/Common';
import RebateMixins from './RebateMixins';

export default {
  setup() { return { v$: useVuelidate() }; },

  components: { MultiStepFormNavigation, ApiLoading, FormField, SectionSeparatorWrapper, DropdownSelect, ReviewAndSubmitComponent, Alert },
  name: 'UpdateRebate',
  mixins: [RebateMixins],

  data() {
    return {
      currentPage: 1,
      pages: {
        REBATE_TIER: {
          pageTitle: 'Rebate tier',
          pageNumber: 1,
          isInvalid: false,
        },
        REVIEW_AND_SUBMIT: {
          pageTitle: 'Review and submit',
          pageNumber: 2,
          isInvalid: true,
        },
      },
      newMyCircumstanceSelected: null,
      newRebateTierSelected: null,
      apiStatus: {
        refData: { loading: false, fail: false },
        premium: { loading: false, fail: false },
        submit: { loading: false, fail: false },
      },
      refData: {},
      submitPolicyError: false,
      RebateTierDisplayWithoutIncomeBrackets,
      ErrorStrings,
    };
  },

  watch: {
    'v$.$invalid': function (value) {
      this.pages.REVIEW_AND_SUBMIT.isInvalid = value;
    },
    currentPage(newVal) {
      if (newVal === this.pages.REVIEW_AND_SUBMIT.pageNumber) {
        this.fetchNewPremium(this.newRebateTierSelected, this.apiStatus.premium);
      }
    },
  },

  beforeMount() {
    this.fetchRefData();
    this.fetchMembershipDetails(this.apiStatus.submit);
  },

  validations() {
    return {
      newMyCircumstanceSelected: { required },
      newRebateTierSelected: {
        required,
        isSameAsCurrent() {
          return this.newRebateTierSelected !== this.currentRebateTier;
        },
      },
    };
  },

  computed: {
    isFirstPage() {
      return this.currentPage === this.pages.REBATE_TIER.pageNumber;
    },

    isSecondPage() {
      return this.currentPage === this.pages.REVIEW_AND_SUBMIT.pageNumber;
    },

    rebateApplicantName() {
      if (this.membershipDetails?.rebateApplicant) {
        const rebateApplicant = this.membershipDetails.rebateApplicant;
        return `${rebateApplicant.firstName}${rebateApplicant.middleName ? ` ${rebateApplicant.middleName}` : ''} ${rebateApplicant.lastName}`;
      }

      return '';
    },

    currentRebateTier() {
      return this.membershipDetails.rebate.rebateTier?.toUpperCase();
    },

    currentDate() {
      return reverseFormatFormDate(getCurrentDate());
    },
  },

  methods: {
    async submitRebateUpdate() {
      this.apiStatus.submit.loading = true;
      this.apiStatus.submit.fail = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);
      try {
        const payload = {
          rebateTier: this.newRebateTierSelected,
          currentDate: getCurrentDate(),
        };

        const response = await RebateServices.submitUpdatedRebateTier(this.memberNumber, payload);

        const params = {
          newPremium: response.newPremium,
          currentPayFrequency: response.currentPayFrequency,
          newRebateTier: this.newRebateTierSelected,
          premiumChangeInformationText: this.refData.rebateSuccessHowYourPremiumHasChangedSectionText.informationText,
          premiumAdjustmentInformationText: this.refData.rebateSuccessYourPaymentAdjustmentSectionText.informationText,
        };

        this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_UPDATE_SUCCESS, params);
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const payload = {
              rebateTier: this.newRebateTierSelected,
              currentDate: getCurrentDate(),
            };

            const request = {
              functionName: "add-update-rebate",
              workItemCategory: "OTHERS",
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };

            await CommonServices.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          // Redirect to success page 
          const params = {
            submitPolicyInProcessHeader: ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.replacePlaceholder(ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.HEADER, 'update rebate'),
            submitPolicyInProcessSubHeader: ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER,
          };

          this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_UPDATE_SUCCESS, params);

        } else {
          this.apiStatus.submit.fail = true;
        }
      } finally {
        this.apiStatus.submit.loading = false;
      }
    },

    async fetchRefData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;
      try {
        this.refData = await RebateServices.getUpdateRebateRefData();
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log('Error fetching update rebate ref data', error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    handleNextButton() {
      if (this.isFirstPage) {
        this.currentPage = this.pages.REVIEW_AND_SUBMIT.pageNumber;
      } else {
        this.submitRebateUpdate();
      }
    },

    handleBackButton() {
      this.currentPage = this.pages.REBATE_TIER.pageNumber;
    },

    reverseFormatFormDate,
  },
};
</script>

<style lang="scss" scoped></style>
